import { useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Monitor, AlertTriangle, Shield, Activity } from "lucide-react";
import { useTenantStats } from "@/api/tenants";
import { useAlerts } from "@/api/alerts";
import { useGlobalCompliance, useGlobalComplianceTiers } from "@/api/compliance";
import { useSyncHealth } from "@/api/sync";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ComplianceBadge } from "@/components/compliance-badge";
import { Skeleton } from "@/components/ui/skeleton";

export function DashboardPage() {
  const { data: stats, isLoading: statsLoading } = useTenantStats();
  const { data: alertsData, isLoading: alertsLoading } = useAlerts({ "filter[status]": "open", pageSize: "5" });
  const { data: compliance, isLoading: complianceLoading } = useGlobalCompliance();
  const { data: tiers, isLoading: tiersLoading } = useGlobalComplianceTiers();
  const { data: syncHealth, isLoading: syncLoading } = useSyncHealth();
  const [showTiers, setShowTiers] = useState(false);

  const totalDevices = stats?.reduce((sum, t) => sum + t.device_count, 0) ?? 0;
  const totalAlerts = stats?.reduce((sum, t) => sum + t.open_alerts, 0) ?? 0;
  const avgCompliance = compliance?.length
    ? Math.round(
        compliance.reduce((sum, c) => sum + (c.average_score ?? 0), 0) / compliance.filter(c => c.average_score !== null).length
      )
    : null;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Dashboard</h1>

      <div className="grid gap-4 md:grid-cols-4">
        <KPICard icon={Building2} label="Tenants" value={stats?.length ?? 0} loading={statsLoading} />
        <button className="text-left" onClick={() => setShowTiers((v) => !v)}>
          <KPICard icon={Monitor} label="Total Devices" value={totalDevices} loading={statsLoading} clickable />
        </button>
        <KPICard icon={AlertTriangle} label="Open Alerts" value={totalAlerts} loading={statsLoading} />
        <KPICard icon={Shield} label="Avg Compliance" value={avgCompliance !== null ? `${avgCompliance}%` : "N/A"} loading={complianceLoading} />
      </div>

      {showTiers && (
        <div className="grid gap-4 md:grid-cols-4">
          {tiersLoading ? (
            Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 w-full rounded-xl" />)
          ) : (
            <>
              <TierCard label="Compliant" count={tiers?.compliant ?? 0} color="text-success" bg="bg-success/10" />
              <TierCard label="At Risk" count={tiers?.at_risk ?? 0} color="text-warning" bg="bg-warning/10" />
              <TierCard label="Non-Compliant" count={tiers?.non_compliant ?? 0} color="text-danger" bg="bg-danger/10" />
              <TierCard label="Unscored" count={tiers?.unscored ?? 0} color="text-text-muted" bg="bg-surface-hover" />
            </>
          )}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Tenants by Compliance</CardTitle>
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
            ) : (
              <div className="space-y-2">
                {stats?.sort((a, b) => (a.avg_compliance ?? 0) - (b.avg_compliance ?? 0)).map((t) => (
                  <Link key={t.id} to={`/tenants/${t.id}`} className="flex items-center justify-between rounded-lg p-3 hover:bg-surface-hover">
                    <div>
                      <p className="font-medium text-text-primary">{t.name}</p>
                      <p className="text-xs text-text-muted">{t.device_count} devices</p>
                    </div>
                    <ComplianceBadge score={t.avg_compliance} />
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent Alerts</CardTitle>
          </CardHeader>
          <CardContent>
            {alertsLoading ? (
              <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
            ) : (
              <div className="space-y-2">
                {alertsData?.data?.map((alert) => (
                  <div key={alert.id} className="flex items-center justify-between rounded-lg p-3 hover:bg-surface-hover">
                    <div>
                      <p className="text-sm font-medium text-text-primary">{alert.title}</p>
                      <p className="text-xs text-text-muted">{alert.alert_type}</p>
                    </div>
                    <Badge variant={alert.severity === "critical" ? "danger" : alert.severity === "warning" ? "warning" : "info"}>
                      {alert.severity}
                    </Badge>
                  </div>
                ))}
                {(!alertsData?.data || alertsData.data.length === 0) && (
                  <p className="py-4 text-center text-sm text-text-muted">No open alerts.</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Sync Health
          </CardTitle>
        </CardHeader>
        <CardContent>
          {syncLoading ? (
            <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : (
            <div className="space-y-2">
              {(syncHealth ?? []).map((s) => (
                <div key={s.integration_source} className="flex items-center justify-between rounded-lg p-3 hover:bg-surface-hover">
                  <div>
                    <p className="text-sm font-medium capitalize text-text-primary">{s.integration_source}</p>
                    <p className="text-xs text-text-muted">
                      {s.last_sync_at ? `Last sync: ${new Date(s.last_sync_at).toLocaleString()}` : "Never synced"}
                    </p>
                  </div>
                  <Badge variant={s.last_job_status === "completed" ? "success" : s.last_job_status === "failed" ? "danger" : "info"}>
                    {s.last_job_status ?? "no data"}
                  </Badge>
                </div>
              ))}
              {(!syncHealth || syncHealth.length === 0) && (
                <p className="py-4 text-center text-sm text-text-muted">No sync data available.</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function KPICard({ icon: Icon, label, value, loading, clickable }: { icon: React.ElementType; label: string; value: number | string; loading: boolean; clickable?: boolean }) {
  return (
    <Card className={clickable ? "cursor-pointer ring-1 ring-transparent hover:ring-accent/30 transition-all" : undefined}>
      <div className="flex items-center gap-4">
        <div className="rounded-lg bg-accent/10 p-2.5 ring-1 ring-accent/10">
          <Icon className="h-5 w-5 text-accent" />
        </div>
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-text-muted">{label}</p>
          {loading ? <Skeleton className="mt-1 h-6 w-16" /> : <p className="text-2xl font-bold text-text-primary">{value}</p>}
        </div>
      </div>
    </Card>
  );
}

function TierCard({ label, count, color, bg }: { label: string; count: number; color: string; bg: string }) {
  return (
    <Card>
      <div className="flex items-center gap-3">
        <div className={`rounded-lg p-2.5 ${bg}`}>
          <span className={`text-lg font-bold ${color}`}>{count}</span>
        </div>
        <p className="text-sm font-medium text-text-muted">{label}</p>
      </div>
    </Card>
  );
}
